/**
 * The FAQ bank as picker options (#202), for the sections editor's Page FAQs block and a suburb page's FAQs (#262).
 * Questions are the display key; the stable _meta.id is what a page stores, so rewording a question does not detach
 * it from the pages using it.
 *
 * The label is prefixed with the item's category name ("Newcastle - Funeral Services - Is there a lot of paperwork
 * required?") because the same question is deliberately duplicated per region, and the question alone cannot tell two
 * of them apart in the list. Categories are records (AccountFaqCategory); pre-#202 items hold a SLUG in `category` and
 * the seed gives each one a record with that slug as its id, so both resolve through the same map.
 */
export function faqOptionsFrom(content: any): Array<{ label: string; value: string }> {
  const list = (raw: any): any[] => (Array.isArray(raw) ? raw : Object.values(raw || {}));
  const live = (x: any) => x && typeof x === 'object' && x?._meta?.id && !x?._meta?.deleted;

  const categoryNames = new Map<string, string>(
    list(content?.faqCategories)
      .filter(live)
      .map((c: any) => [String(c._meta.id), String(c.name || c._meta.id)])
  );
  // An unknown id still prefixes with the raw value rather than dropping it: a stale category is worth seeing.
  const catName = (f: any) =>
    f.category ? (categoryNames.get(String(f.category)) ?? String(f.category)) : '';

  return (
    list(content?.faqs)
      .filter(live)
      // Category first so the list reads in groups: with the same question duplicated across regions, an
      // order-only sort interleaves them. Ungrouped items sort last, so a blank prefix doesn't head the list.
      .sort((x: any, y: any) => {
        const cx = catName(x);
        const cy = catName(y);
        if (cx !== cy) return (cx || '\uffff').localeCompare(cy || '\uffff');
        return (x.order ?? 0) - (y.order ?? 0);
      })
      .map((f: any) => {
        const question = String(f.question || '(no question yet)');
        const category = catName(f);
        return {
          label: category ? `${category} - ${question}` : question,
          value: String(f._meta.id),
        };
      })
  );
}
